import { useCallback, useRef } from 'react';
import type { U2000Pagination, U2000SearchPayload, U2000SearchResult } from '../../components/tables/basic/U2000BasicTable';
import { U2000GridStateEnumPaginationDirection } from '../../reducer/formReducer/U2000GridState';
import type { U2000SearchParameter } from '../../reducer/formReducer/U2000SearchState';

export interface U2000SearchHandlersParams<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- doSearchFn agnostique par UL
  doSearchFn: (...args: any[]) => Promise<any>;
  onSuccess?: (searchResult: U2000SearchResult<T>, direction: U2000GridStateEnumPaginationDirection) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- erreur axios / API
  onError?: (errors: { error: any }) => void;
}

export interface U2000SearchHandlersReturn {
  handleSearch: (
    searchParameter: U2000SearchParameter,
    pagination: U2000Pagination,
    direction: U2000GridStateEnumPaginationDirection
  ) => Promise<void>;
}

export function useU2000SearchHandlers<T>({ doSearchFn, onSuccess, onError }: U2000SearchHandlersParams<T>): U2000SearchHandlersReturn {
  const doSearchFnRef = useRef(doSearchFn);
  doSearchFnRef.current = doSearchFn;

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const handleSearch = useCallback(
    async (
      searchParameter: U2000SearchParameter,
      pagination: U2000Pagination,
      direction: U2000GridStateEnumPaginationDirection = U2000GridStateEnumPaginationDirection.NEXTPAGE
    ) => {
      try {
        const searchPayload: U2000SearchPayload = {
          filter: searchParameter,
          pagination,
        };

        const response = await doSearchFnRef.current(searchPayload);
        const searchResult: U2000SearchResult<T> = {} as U2000SearchResult<T>;
        searchResult.input = response.data.input.filter as U2000SearchParameter;
        searchResult.pagination = response.data.input.pagination;
        searchResult.data = response.data.results as unknown as T[];
        searchResult.nbRecord = response.data.queryRowsCount as number;

        if (onSuccessRef.current) {
          onSuccessRef.current(searchResult as unknown as U2000SearchResult<T>, direction);
        }
      } catch (error) {
        if (onErrorRef.current) {
          onErrorRef.current({ error });
        }
      }
    },
    []
  );

  return { handleSearch };
}
